import { useState, useEffect, useCallback, useRef, type MouseEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { InfoTooltip } from '@/components/help/InfoTooltip';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AIInput } from '@/components/ui/ai-input';
import { AIRichTextEditor } from '@/components/ui/ai-rich-text-editor';
import { richHtmlToPayload } from '@/lib/html-utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import type {
  Campaign,
  CampaignType,
  CampaignChannel,
  ContentStatus,
  Supplier,
} from '@/types';
import type { ApiResponse } from '@/types';
import { cn, formatCurrency, formatDate, statusColors } from '@/lib/utils';
import { api } from '@/lib/api';
import { getAllPages } from '@/lib/paginated-data';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Megaphone, LayoutGrid, Rows3, Rocket, Target, Wallet2, X, Loader2, MoreVertical } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';
import { useHashFilters } from '@/hooks/useHashFilters';
import { ContextMenu } from '@/components/common/ContextMenu';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';

const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  B2B: 'B2B',
  B2C: 'B2C',
  mixed: 'Gemischt',
};

const CHANNEL_LABELS: Record<CampaignChannel, string> = {
  email: 'E-Mail',
  google: 'Google',
  linkedin: 'LinkedIn',
  print: 'Print',
  social: 'Social',
  multi: 'Multi',
  other: 'Sonstige',
};

const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Entwurf',
  review: 'Review',
  approved: 'Genehmigt',
  published: 'Veröffentlicht',
  rejected: 'Abgelehnt',
};
const CAMPAIGNS_HERO_IMAGE =
  'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1400&q=80';

type PortalSupplierSummary = { id: string; name: string; company?: string | null; contact_email?: string | null };

function CampaignCardActionsMenu({
  onOpen,
  onStatusReview,
  onStatusDraft,
  onDeactivate,
}: {
  onOpen: () => void;
  onStatusReview: () => void;
  onStatusDraft: () => void;
  onDeactivate: () => void;
}) {
  const stopCardNav = (e: MouseEvent) => {
    e.stopPropagation();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted hover:text-foreground"
          aria-label="Kampagnen-Aktionen"
          onClick={stopCardNav}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={stopCardNav}>
        <DropdownMenuItem onClick={onOpen}>Öffnen</DropdownMenuItem>
        <DropdownMenuItem onClick={onStatusReview}>Status: Review</DropdownMenuItem>
        <DropdownMenuItem onClick={onStatusDraft}>Status: Entwurf</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-danger focus:text-danger"
          onClick={onDeactivate}
        >
          Deaktivieren…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CampaignsPage() {
  const confirmDialog = useConfirmDialog();
  const roles = useAuthStore((s) => s.user?.roles) as string[] | undefined;
  const supplierPortalUser = isSupplierPortalUser(roles);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [listActionError, setListActionError] = useState<string | null>(null);
  const { filters, setFilter } = useHashFilters({
    type: 'all' as CampaignType | 'all',
    channel: 'all' as CampaignChannel | 'all',
    status: 'all' as ContentStatus | 'all',
    supplier_id: '',
    view: 'cards' as 'cards' | 'table',
    search: '',
    sort_by: 'created_at',
    order: 'desc',
  });
  const [searchInput, setSearchInput] = useState(filters.search);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [planOptions, setPlanOptions] = useState<{ id: string; title: string }[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [createForm, setCreateForm] = useState({
    plan_id: '',
    name: '',
    type: 'B2C' as CampaignType,
    channel: 'multi' as CampaignChannel,
    description: '',
  });
  /** Required for supplier-portal users (API); optional for internal users (link campaign to supplier). */
  const [createSupplierId, setCreateSupplierId] = useState('');
  const [portalSuppliers, setPortalSuppliers] = useState<PortalSupplierSummary[]>([]);
  const loadRequest = useRef(0);

  const loadCampaigns = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    setListActionError(null);
    try {
      const params = new URLSearchParams();
      if (filters.type !== 'all') params.set('type', filters.type);
      if (filters.channel !== 'all') params.set('channel', filters.channel);
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.supplier_id) params.set('supplier_id', filters.supplier_id);
      if (filters.search) params.set('search', filters.search);
      params.set('sort_by', filters.sort_by);
      params.set('order', filters.order);
      const campaigns = await getAllPages<Campaign>(
        `/campaigns?${params.toString()}`
      );
      if (request === loadRequest.current) setCampaigns(campaigns);
    } catch (error) {
      if (request !== loadRequest.current) return;
      setCampaigns([]);
      setListActionError(error instanceof Error ? error.message : 'Kampagnen konnten nicht geladen werden.');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void loadCampaigns();
    return () => { loadRequest.current += 1; };
  }, [loadCampaigns]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilter('search', searchInput);
      }
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [filters.search, searchInput, setFilter]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.get<ApiResponse<{ id: string; title: string }[]>>('/marketing-plans?limit=100');
        const plans = Array.isArray(res.data) ? res.data : [];
        const firstPlanId = plans[0]?.id;
        setPlanOptions(plans);
        if (firstPlanId) {
          setCreateForm((prev) => prev.plan_id ? prev : ({ ...prev, plan_id: firstPlanId }));
        }
      } catch {
        setPlanOptions([]);
      }
    };
    loadPlans();
  }, []);

  useEffect(() => {
    if (supplierPortalUser) return;
    const loadSuppliers = async () => {
      try {
        const res = await api.get<ApiResponse<Supplier[]>>('/suppliers');
        setSupplierOptions(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSupplierOptions([]);
      }
    };
    loadSuppliers();
  }, [supplierPortalUser]);

  useEffect(() => {
    if (!supplierPortalUser) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          success?: boolean;
          data?: { suppliers?: PortalSupplierSummary[] };
        }>('/supplier-portal/summary');
        const list = (res.success !== false && res.data ? res.data.suppliers : null) ?? [];
        if (cancelled) return;
        setPortalSuppliers(list);
        if (list.length === 1) {
          const only = list[0];
          if (only) setCreateSupplierId(only.id);
        }
      } catch {
        if (!cancelled) setPortalSuppliers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supplierPortalUser]);

  useEffect(() => {
    const shouldOpenCreate = window.localStorage.getItem('leftclick_open_create_campaign') === '1';
    const prefSupplierId = window.localStorage.getItem('leftclick_create_campaign_supplier_id');
    if (prefSupplierId) {
      setCreateSupplierId(prefSupplierId);
      window.localStorage.removeItem('leftclick_create_campaign_supplier_id');
    }
    if (shouldOpenCreate) {
      setCreateDialogOpen(true);
      window.localStorage.removeItem('leftclick_open_create_campaign');
    }
  }, []);

  const openDetailPage = (campaignId: string) => {
    window.location.hash = `/campaigns/${encodeURIComponent(campaignId)}`;
  };

  const changeCampaignStatus = async (campaignId: string, status: 'review' | 'draft') => {
    setListActionError(null);
    try {
      await api.patch(`/campaigns/${campaignId}`, { status });
      await loadCampaigns();
    } catch (error) {
      setListActionError(error instanceof Error ? error.message : 'Status konnte nicht geändert werden.');
    }
  };

  const handleDeleteCampaignFromMenu = async (c: Campaign) => {
    const confirmed = await confirmDialog({
      title: 'Kampagne deaktivieren?',
      description: `«${c.name}» wird aus der Übersicht entfernt — unabhängig vom Status (z. B. Entwurf). Alle mit dieser Kampagne verknüpften Aktivitäten werden ebenfalls deaktiviert und tauchen in Plan- und Aktivitätslisten nicht mehr auf.`,
      confirmText: 'Deaktivieren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setListActionError(null);
    try {
      const res = await api.patch<ApiResponse<{ activities_deactivated?: number }>>(
        `/campaigns/${c.id}/deactivate`
      );
      if (!res.success) {
        throw new Error(res.error ?? 'Kampagne konnte nicht deaktiviert werden.');
      }
      const n = res.data?.activities_deactivated;
      if (typeof n === 'number' && n > 0) {
        toast.success('Kampagne deaktiviert.', {
          description: `${n} verknüpfte Aktivität${n === 1 ? '' : 'en'} ebenfalls entfernt.`,
        });
      } else {
        toast.success('Kampagne deaktiviert.');
      }
      await loadCampaigns();
    } catch (error) {
      setListActionError(error instanceof Error ? error.message : 'Kampagne konnte nicht deaktiviert werden.');
    }
  };

  const budgetProgress = (c: Campaign) => {
    if (c.budget_total <= 0) return 0;
    const spent = (c.budget_own ?? 0) + (c.budget_supplier ?? 0);
    return Math.min(100, (spent / c.budget_total) * 100);
  };
  const totalBudget = campaigns.reduce((sum, campaign) => sum + (campaign.budget_total ?? 0), 0);
  const activeCount = campaigns.filter((campaign) => campaign.status === 'published' || campaign.status === 'approved').length;

  const handleCreateCampaign = async () => {
    setCreateError(null);
    if (!createForm.plan_id) {
      setCreateError('Bitte einen Marketingplan auswählen.');
      return;
    }
    if (!createForm.name.trim()) {
      setCreateError('Bitte einen Kampagnennamen eingeben.');
      return;
    }
    if (supplierPortalUser && !createSupplierId) {
      setCreateError('Bitte einen Lieferanten wählen oder prüfen Sie Ihre Portal-Zuordnung.');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        plan_id: createForm.plan_id,
        name: createForm.name.trim(),
        type: createForm.type,
        channel: createForm.channel,
        description: richHtmlToPayload(createForm.description),
        description_md: richHtmlToPayload(createForm.description),
      };
      if (createSupplierId) {
        payload.supplier_id = createSupplierId;
      }
      await api.post<ApiResponse<Campaign>>('/campaigns', payload);
      setCreateDialogOpen(false);
      setCreateForm((prev) => ({
        plan_id: prev.plan_id || planOptions[0]?.id || '',
        name: '',
        type: 'B2C',
        channel: 'multi',
        description: '',
      }));
      if (supplierPortalUser && portalSuppliers.length === 1) {
        const only = portalSuppliers[0];
        if (only) setCreateSupplierId(only.id);
      } else if (!supplierPortalUser) {
        setCreateSupplierId('');
      }
      await loadCampaigns();
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Kampagne konnte nicht erstellt werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 p-6">
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm dark:bg-background">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14] dark:opacity-[0.1]"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.96), rgba(255,255,255,0.78)), url(${CAMPAIGNS_HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative p-5 lg:p-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent-soft-foreground">
              <Rocket className="h-3.5 w-3.5" />
              Kampagnen Hub
            </div>
            <h2 className="mt-3 text-xl font-semibold text-foreground">
              Kampagnen mit mehr Überblick
            </h2>
            <p className="mt-1 text-sm text-muted">
              Plane, filtere und steuere alle Kampagnen in einer zentralen Ansicht.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><Megaphone className="h-3 w-3" /> Gesamt</p>
                <p className="text-lg font-semibold text-foreground">{campaigns.length}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><Target className="h-3 w-3" /> Aktiv</p>
                <p className="text-lg font-semibold text-foreground">{activeCount}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><Wallet2 className="h-3 w-3" /> Budget</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(totalBudget)}</p>
              </div>
            </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Kampagnen</h1>
        </div>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0">
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Neue Kampagne
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-sm">
            Legt eine neue Kampagne im gewählten Marketingplan an. Titel, Budget und Kanal anschliessend im Dialog
            pflegen.
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted">
          <span>Filter & Suche</span>
          <InfoTooltip text="Suche durchsucht Namen. Typ, Kanal und Status filtern die Liste. Zulieferer: Co-Marketing-Kampagnen. Sortierung und Reihenfolge steuern die Darstellung." />
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Kampagnen suchen..."
          className="w-[220px]"
        />
        <Select
          value={filters.type}
          onValueChange={(v) => setFilter('type', v as typeof filters.type)}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Typ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            {(Object.keys(CAMPAIGN_TYPE_LABELS) as CampaignType[]).map((k) => (
              <SelectItem key={k} value={k}>
                {CAMPAIGN_TYPE_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.channel}
          onValueChange={(v) => setFilter('channel', v as typeof filters.channel)}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Kanal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kanäle</SelectItem>
            {(Object.keys(CHANNEL_LABELS) as CampaignChannel[]).map((k) => (
              <SelectItem key={k} value={k}>
                {CHANNEL_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.status}
          onValueChange={(v) => setFilter('status', v as typeof filters.status)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {(Object.keys(CONTENT_STATUS_LABELS) as ContentStatus[]).map((k) => (
              <SelectItem key={k} value={k}>
                {CONTENT_STATUS_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.supplier_id || 'all'} onValueChange={(v) => setFilter('supplier_id', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Zulieferer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Zulieferer</SelectItem>
            {supplierOptions.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.sort_by} onValueChange={(v) => setFilter('sort_by', v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Sortierung" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Erstellt am</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="budget_total">Budget</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.order} onValueChange={(v) => setFilter('order', v as 'asc' | 'desc')}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Absteigend</SelectItem>
            <SelectItem value="asc">Aufsteigend</SelectItem>
          </SelectContent>
        </Select>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="ml-auto inline-flex items-center rounded-md border border-border p-1">
              <Button
                type="button"
                variant={filters.view === 'cards' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('view', 'cards')}
                className="h-8 px-2"
              >
                <LayoutGrid className="h-4 w-4" />
                Cards
              </Button>
              <Button
                type="button"
                variant={filters.view === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('view', 'table')}
                className="h-8 px-2"
              >
                <Rows3 className="h-4 w-4" />
                Tabelle
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-sm" side="bottom">
            Karten: kompakt für Überblick. Tabelle: mehr Spalten und Sortierung auf einen Blick.
          </TooltipContent>
        </Tooltip>
      </div>
      </div>

      {listActionError && (
        <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {listActionError}
        </p>
      )}

      {loading && campaigns.length === 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-44 animate-pulse rounded-xl border border-border bg-card-hover dark:bg-card"
            />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Megaphone className="mb-4 h-12 w-12 text-muted" />
            <p className="mb-4 text-muted">
              Keine Kampagnen gefunden.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Neue Kampagne anlegen
            </Button>
          </CardContent>
        </Card>
      ) : filters.view === 'table' ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-background dark:bg-card/40">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted">Name</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Typ</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Kanal</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Zulieferer</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Budget</th>
                    <th className="px-4 py-3 text-left font-medium text-muted">Zeitraum</th>
                    <th className="w-12 px-2 py-3 text-right font-medium text-muted">
                      <span className="sr-only">Aktionen</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {campaigns.map((campaign) => (
                    <tr
                      key={campaign.id}
                      className="cursor-pointer transition-colors hover:bg-background dark:hover:bg-card/40"
                      onClick={() => openDetailPage(campaign.id)}
                    >
                      <td className="px-4 py-3 font-medium">{campaign.name}</td>
                      <td className="px-4 py-3">{CAMPAIGN_TYPE_LABELS[campaign.type]}</td>
                      <td className="px-4 py-3">{CHANNEL_LABELS[campaign.channel]}</td>
                      <td className="px-4 py-3">
                        <Badge className={cn(statusColors[campaign.status])}>
                          {CONTENT_STATUS_LABELS[campaign.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{campaign.supplier?.name ?? '—'}</td>
                      <td className="px-4 py-3">{formatCurrency(campaign.budget_total)}</td>
                      <td className="px-4 py-3 text-muted">
                        {campaign.start_date
                          ? `${formatDate(campaign.start_date)}${campaign.end_date ? ` – ${formatDate(campaign.end_date)}` : ''}`
                          : '—'}
                      </td>
                      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <CampaignCardActionsMenu
                          onOpen={() => openDetailPage(campaign.id)}
                          onStatusReview={() => void changeCampaignStatus(campaign.id, 'review')}
                          onStatusDraft={() => void changeCampaignStatus(campaign.id, 'draft')}
                          onDeactivate={() => void handleDeleteCampaignFromMenu(campaign)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => (
            <ContextMenu
              key={campaign.id}
              actions={[
                { label: 'Öffnen', onClick: () => openDetailPage(campaign.id) },
                { label: 'Status auf Review', onClick: () => void changeCampaignStatus(campaign.id, 'review') },
                { label: 'Status auf Entwurf', onClick: () => void changeCampaignStatus(campaign.id, 'draft') },
                {
                  label: 'Deaktivieren…',
                  destructive: true,
                  separatorBefore: true,
                  onClick: () => void handleDeleteCampaignFromMenu(campaign),
                },
              ]}
            >
              <Card
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => openDetailPage(campaign.id)}
              >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="min-w-0 flex-1 text-base">{campaign.name}</CardTitle>
                  <div className="flex shrink-0 items-start gap-1">
                    <Badge className={cn(statusColors[campaign.status])}>
                      {CONTENT_STATUS_LABELS[campaign.status]}
                    </Badge>
                    <CampaignCardActionsMenu
                      onOpen={() => openDetailPage(campaign.id)}
                      onStatusReview={() => void changeCampaignStatus(campaign.id, 'review')}
                      onStatusDraft={() => void changeCampaignStatus(campaign.id, 'draft')}
                      onDeactivate={() => void handleDeleteCampaignFromMenu(campaign)}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="secondary">
                    {CAMPAIGN_TYPE_LABELS[campaign.type]}
                  </Badge>
                  <Badge variant="outline">
                    {CHANNEL_LABELS[campaign.channel]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted">Budget</p>
                  <Progress value={budgetProgress(campaign)} className="h-2" />
                  <p className="mt-1 text-sm">
                    {formatCurrency(campaign.budget_total)} gesamt
                  </p>
                </div>
                {campaign.start_date && (
                  <p className="text-sm text-muted">
                    {formatDate(campaign.start_date)}
                    {campaign.end_date && ` – ${formatDate(campaign.end_date)}`}
                  </p>
                )}
              </CardContent>
              </Card>
            </ContextMenu>
          ))}
        </div>
      )}

      {/* Create campaign dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Neue Kampagne</DialogTitle>
            <DialogDescription>
              Kampagne mit Plan, Typ und Kanal erstellen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {createError && (
              <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {createError}
              </p>
            )}
            <div className="grid gap-2">
              <Label>Marketingplan</Label>
              <Select
                value={createForm.plan_id}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, plan_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Plan wählen" />
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {supplierPortalUser && portalSuppliers.length > 1 && (
              <div className="grid gap-2">
                <Label>Lieferant</Label>
                <Select
                  value={createSupplierId || '__none__'}
                  onValueChange={(v) => setCreateSupplierId(v === '__none__' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lieferant wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Bitte wählen…</SelectItem>
                    {portalSuppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {supplierPortalUser && portalSuppliers.length === 1 && portalSuppliers[0] && (
              <p className="text-sm text-muted">
                Lieferant:{' '}
                <span className="font-medium text-foreground">{portalSuppliers[0].name}</span>
              </p>
            )}
            {supplierPortalUser && portalSuppliers.length === 0 && (
              <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
                Ihrem Konto ist kein Lieferant zugeordnet. Bitte kontaktieren Sie die Administration.
              </p>
            )}
            {!supplierPortalUser && supplierOptions.length > 0 && (
              <div className="grid gap-2">
                <Label>Lieferant (optional)</Label>
                <Select
                  value={createSupplierId || 'none'}
                  onValueChange={(v) => setCreateSupplierId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kein Lieferant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Lieferant</SelectItem>
                    {supplierOptions.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-2">
              <Label>Name</Label>
              <AIInput
                placeholder="Kampagnenname"
                value={createForm.name}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                onValueChange={(v) => setCreateForm((prev) => ({ ...prev, name: v }))}
                aiFieldLabel="Kampagnenname"
                aiContext={`Marketing-Kampagne ${createForm.type} über Kanal ${createForm.channel}`}
              />
            </div>
            <div className="grid gap-2">
              <Label>Typ</Label>
              <Select
                value={createForm.type}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, type: value as CampaignType }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="B2B / B2C / Gemischt" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CAMPAIGN_TYPE_LABELS) as CampaignType[]).map(
                    (k) => (
                      <SelectItem key={k} value={k}>
                        {CAMPAIGN_TYPE_LABELS[k]}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Kanal</Label>
              <Select
                value={createForm.channel}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, channel: value as CampaignChannel }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kanal wählen" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CHANNEL_LABELS) as CampaignChannel[]).map(
                    (k) => (
                      <SelectItem key={k} value={k}>
                        {CHANNEL_LABELS[k]}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Beschreibung</Label>
              <AIRichTextEditor
                value={createForm.description}
                onValueChange={(v) => setCreateForm((prev) => ({ ...prev, description: v }))}
                placeholder="Kurze Zielbeschreibung der Kampagne"
                aiFieldLabel="Kampagnenbeschreibung"
                aiContext={`${createForm.name ? `Kampagne: ${createForm.name}, ` : ''}Typ: ${createForm.type}, Kanal: ${createForm.channel}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button disabled={saving} onClick={handleCreateCampaign}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Erstellen…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
